import { combineReducers } from 'redux';

import {
  FORGOT_PASSWORD_FULFILLED,
  FORGOT_PASSWORD_PENDING,
  FORGOT_PASSWORD_REJECTED,
  GET_ME_FULFILLED,
  GET_ME_PENDING,
  GET_ME_REJECTED,
  LOGIN_USER_FULFILLED,
  LOGIN_USER_PENDING,
  LOGIN_USER_REJECTED,
  LOGOUT_USER_FULFILLED,
  LOGOUT_USER_PENDING,
  LOGOUT_USER_REJECTED,
  SIGNUP_USER_FULFILLED,
  SIGNUP_USER_PENDING,
  SIGNUP_USER_REJECTED,
} from '../types';

// State interfaces
interface MeState {
  data: any | null;
  isFetching: boolean;
  message?: string;
}

interface LoginState {
  data: any | null;
  isFetching: boolean;
  message?: string;
}

interface LogoutState {
  message: string | null;
  isFetching: boolean;
}

interface SignupState {
  message: string | null;
  isFetching: boolean;
}

interface ForgotPasswordState {
  message: string | null;
  isFetching: boolean;
}

interface AuthState {
  me: MeState;
  login: LoginState;
  logout: LogoutState;
  signup: SignupState;
  forgotPassword: ForgotPasswordState;
}

// Action interfaces
interface PendingAction {
  type: typeof GET_ME_PENDING | typeof LOGIN_USER_PENDING | typeof LOGOUT_USER_PENDING | 
        typeof SIGNUP_USER_PENDING | typeof FORGOT_PASSWORD_PENDING;
}

interface GetMeFulfilledAction {
  type: typeof GET_ME_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetMeRejectedAction {
  type: typeof GET_ME_REJECTED;
  payload: {
    message: string;
  };
}

interface LoginUserFulfilledAction {
  type: typeof LOGIN_USER_FULFILLED;
  payload: any;
}

interface LoginUserRejectedAction {
  type: typeof LOGIN_USER_REJECTED;
  payload: {
    message: string;
  };
}

interface MessageAction {
  type: typeof LOGOUT_USER_FULFILLED | typeof LOGOUT_USER_REJECTED |
        typeof SIGNUP_USER_FULFILLED | typeof SIGNUP_USER_REJECTED |
        typeof FORGOT_PASSWORD_FULFILLED | typeof FORGOT_PASSWORD_REJECTED;
  payload: {
    message: string;
  };
}

type MeAction = PendingAction | GetMeFulfilledAction | GetMeRejectedAction;
type LoginAction = PendingAction | LoginUserFulfilledAction | LoginUserRejectedAction;
type LogoutAction = PendingAction | MessageAction;
type SignupAction = PendingAction | MessageAction;
type ForgotPasswordAction = PendingAction | MessageAction;

const INITIAL_STATE = {
  ME: {
    data: null,
    isFetching: false,
  },
  LOGIN: {
    data: null,
    isFetching: false,
  },
  LOGOUT: {
    message: null,
    isFetching: false,
  },
  SIGNUP: {
    message: null,
    isFetching: false,
  },
  FORGOT_PASSWORD: {
    message: null,
    isFetching: false,
  },
};

const me = (state: MeState = INITIAL_STATE.ME, action: MeAction): MeState => {
  switch (action.type) {
    case GET_ME_PENDING:
      return {
        ...INITIAL_STATE.ME,
        isFetching: true,
      };
    case GET_ME_FULFILLED:
      const { data } = action.payload;
      return {
        data,
        isFetching: false,
      };
    case GET_ME_REJECTED: {
      const { message } = action.payload;

      return {
        data: null,
        message,
        isFetching: false,
      };
    }
    default:
      return state;
  }
};

const login = (state: LoginState = INITIAL_STATE.LOGIN, action: LoginAction): LoginState => {
  switch (action.type) {
    case LOGIN_USER_PENDING:
      return {
        ...INITIAL_STATE.LOGIN,
        isFetching: true,
      };
    case LOGIN_USER_FULFILLED:
      return {
        data: action.payload,
        isFetching: false,
      };
    case LOGIN_USER_REJECTED: {
      const { message } = action.payload;

      return {
        data: null,
        message,
        isFetching: false,
      };
    }
    default:
      return state;
  }
};

const signup = (state: SignupState = INITIAL_STATE.SIGNUP, action: SignupAction): SignupState => {
  switch (action.type) {
    case SIGNUP_USER_PENDING:
      return {
        ...INITIAL_STATE.SIGNUP,
        isFetching: true,
      };
    case SIGNUP_USER_FULFILLED:
    case SIGNUP_USER_REJECTED: {
      const { message } = action.payload;
      return {
        message,
        isFetching: false,
      };
    }
    default:
      return state;
  }
};

const logout = (state: LogoutState = INITIAL_STATE.LOGOUT, action: LogoutAction): LogoutState => {
  switch (action.type) {
    case LOGOUT_USER_PENDING:
      return {
        ...INITIAL_STATE.LOGOUT,
        isFetching: true,
      };
    case LOGOUT_USER_FULFILLED:
    case LOGOUT_USER_REJECTED: {
      const { message } = action.payload;

      return {
        message,
        isFetching: false,
      };
    }
    default:
      return state;
  }
};

const forgotPassword = (state: ForgotPasswordState = INITIAL_STATE.FORGOT_PASSWORD, action: ForgotPasswordAction): ForgotPasswordState => {
  switch (action.type) {
    case FORGOT_PASSWORD_PENDING:
      return {
        ...INITIAL_STATE.FORGOT_PASSWORD,
        isFetching: true,
      };
    case FORGOT_PASSWORD_FULFILLED:
    case FORGOT_PASSWORD_REJECTED: {
      const { message } = action.payload;

      return {
        message,
        isFetching: false,
      };
    }
    default:
      return state;
  }
};

export default combineReducers<AuthState>({
  forgotPassword,
  login,
  logout,
  me,
  signup,
});